import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  getDocsFromServer,
  deleteDoc,
  query,
  where,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../config/firebase";

/* ─── Service-specific default availability slots ─────────────── */
function getServiceDefaultSlots(serviceId) {
  switch (serviceId) {
    case "jet-ski":
      return [
        { time: "09:00 - 10:00", capacity: 2, booked: 0 },
        { time: "11:00 - 12:00", capacity: 2, booked: 0 },
        { time: "16:00 - 17:00", capacity: 2, booked: 0 },
        { time: "18:00 - 19:00", capacity: 2, booked: 0 },
      ];
    case "luxury-yacht":
      return [
        { time: "10:00 - 14:00", capacity: 10, booked: 0 },
        { time: "16:00 - 20:00", capacity: 10, booked: 0 },
      ];
    case "secret-beach-escape":
      return [
        { time: "09:00 - 14:00", capacity: 11, booked: 0 },
        { time: "10:00 - 15:00", capacity: 11, booked: 0 },
      ];
    case "bayliner":
      return [
        { time: "09:00 - 09:30", capacity: 5, booked: 0 },
        { time: "09:00 - 10:00", capacity: 5, booked: 0 },
        { time: "10:00 - 10:30", capacity: 5, booked: 0 },
        { time: "11:00 - 11:30", capacity: 5, booked: 0 },
        { time: "11:00 - 12:00", capacity: 5, booked: 0 },
        { time: "14:00 - 14:30", capacity: 5, booked: 0 },
        { time: "14:00 - 15:00", capacity: 5, booked: 0 },
        { time: "15:00 - 15:30", capacity: 5, booked: 0 },
        { time: "16:00 - 16:30", capacity: 5, booked: 0 },
        { time: "16:00 - 17:00", capacity: 5, booked: 0 },
        { time: "17:00 - 17:30", capacity: 5, booked: 0 },
      ];
    default: // speedboat — ALL possible slots across all 4 packages
      return [
        { time: "09:00 - 10:00", capacity: 11, booked: 0 },
        { time: "09:00 - 10:30", capacity: 11, booked: 0 },
        { time: "09:00 - 11:00", capacity: 11, booked: 0 },
        { time: "11:00 - 12:00", capacity: 11, booked: 0 },
        { time: "15:00 - 16:00", capacity: 11, booked: 0 },
        { time: "17:00 - 18:30", capacity: 11, booked: 0 },
        { time: "18:00 - 19:00", capacity: 11, booked: 0 },
        { time: "18:00 - 20:00", capacity: 11, booked: 0 },
      ];
  }
}

/**
 * AdminCalendarService
 * Manages all admin-level calendar and booking operations:
 * - Block / unblock dates
 * - Override time-slot capacities per day
 * - Fetch all bookings (with optional filters)
 * - Confirm / cancel / complete any booking
 * - Aggregate day-level stats for the calendar view
 */
class AdminCalendarService {
  constructor() {
    this.bookingsRef = collection(db, "bookings");
    this.availabilityRef = collection(db, "availability");
    this.blockedDatesRef = collection(db, "blockedDates");
    this.servicesRef = collection(db, "services");
    this.externalBookingsRef = collection(db, "externalBookings");
  }

  /* ─────────────────────────────────────────────────────────
   * BLOCKED DATES
   * ───────────────────────────────────────────────────────── */

  /** Block one or more dates for a service (or all services if serviceId === "all") */
  async blockDates(dates, reason = "", serviceId = "all") {
    const batch = writeBatch(db);
    for (const date of dates) {
      const id = `${serviceId}_${date}`;
      const ref = doc(this.blockedDatesRef, id);
      batch.set(ref, {
        id,
        serviceId,
        date,
        reason,
        blocked: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }
    await batch.commit();
    return { success: true, count: dates.length };
  }

  /** Unblock a date for a service */
  async unblockDate(date, serviceId = "all") {
    const id = `${serviceId}_${date}`;
    const ref = doc(this.blockedDatesRef, id);
    await deleteDoc(ref);
    return { success: true };
  }

  /** Get all blocked dates (optionally filtered by serviceId) */
  async getBlockedDates(serviceId = null) {
    try {
      let q = serviceId
        ? query(this.blockedDatesRef, where("serviceId", "==", serviceId))
        : this.blockedDatesRef;
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error("Error fetching blocked dates:", err);
      return [];
    }
  }

  /** Check if a specific date+serviceId is blocked */
  async isDateBlocked(date, serviceId = "all") {
    const id = `${serviceId}_${date}`;
    const ref = doc(this.blockedDatesRef, id);
    const snap = await getDoc(ref);
    if (snap.exists()) return true;
    // Also check "all"-services block
    if (serviceId !== "all") {
      const allRef = doc(this.blockedDatesRef, `all_${date}`);
      const allSnap = await getDoc(allRef);
      return allSnap.exists();
    }
    return false;
  }

  /* ─────────────────────────────────────────────────────────
   * AVAILABILITY / SLOT CAPACITY OVERRIDES
   * ───────────────────────────────────────────────────────── */

  /** Get or create availability document for a service + date */
  async getOrCreateAvailability(serviceId, dateStr) {
    const q = query(
      this.availabilityRef,
      where("serviceId", "==", serviceId),
      where("date", "==", dateStr),
    );
    const snap = await getDocs(q);
    if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };

    const defaultSlots = getServiceDefaultSlots(serviceId);
    const totalCapacity = defaultSlots.reduce((sum, s) => sum + s.capacity, 0);
    const newDoc = {
      serviceId,
      date: dateStr,
      totalCapacity,
      bookedCapacity: 0,
      timeSlots: defaultSlots,
      createdAt: Timestamp.now(),
    };
    const ref = await addDoc(this.availabilityRef, newDoc);
    return { id: ref.id, ...newDoc };
  }

  /** Update the capacity of a specific time slot for a given date + service */
  async updateSlotCapacity(serviceId, dateStr, slotTime, newCapacity) {
    const avail = await this.getOrCreateAvailability(serviceId, dateStr);
    const slots = avail.timeSlots.map((s) =>
      s.time === slotTime ? { ...s, capacity: newCapacity } : s,
    );
    const totalCapacity = slots.reduce((sum, s) => sum + s.capacity, 0);
    const ref = doc(this.availabilityRef, avail.id);
    await updateDoc(ref, {
      timeSlots: slots,
      totalCapacity,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  }

  /** Add a new time slot to a date */
  async addTimeSlot(serviceId, dateStr, slotTime, capacity = 9) {
    const avail = await this.getOrCreateAvailability(serviceId, dateStr);
    const exists = avail.timeSlots.find((s) => s.time === slotTime);
    if (exists) throw new Error("Time slot already exists");
    const slots = [
      ...avail.timeSlots,
      { time: slotTime, capacity, booked: 0 },
    ].sort((a, b) => a.time.localeCompare(b.time));
    const totalCapacity = slots.reduce((sum, s) => sum + s.capacity, 0);
    const ref = doc(this.availabilityRef, avail.id);
    await updateDoc(ref, {
      timeSlots: slots,
      totalCapacity,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  }

  /** Remove a time slot from a date */
  async removeTimeSlot(serviceId, dateStr, slotTime) {
    const avail = await this.getOrCreateAvailability(serviceId, dateStr);
    const slot = avail.timeSlots.find((s) => s.time === slotTime);
    if (slot?.booked > 0)
      throw new Error("Cannot remove a slot that has active bookings");
    const slots = avail.timeSlots.filter((s) => s.time !== slotTime);
    const totalCapacity = slots.reduce((sum, s) => sum + s.capacity, 0);
    // Also remove from blockedSlots if present
    const blockedSlots = (avail.blockedSlots || []).filter(
      (t) => t !== slotTime,
    );
    const ref = doc(this.availabilityRef, avail.id);
    await updateDoc(ref, {
      timeSlots: slots,
      totalCapacity,
      blockedSlots,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  }

  /**
   * Block a specific time slot so it cannot be booked.
   * Stores blocked slot times in availability.blockedSlots array.
   */
  async blockSlot(serviceId, dateStr, slotTime, reason = "") {
    const avail = await this.getOrCreateAvailability(serviceId, dateStr);
    const blocked = new Set(avail.blockedSlots || []);
    blocked.add(slotTime);
    const ref = doc(this.availabilityRef, avail.id);
    await updateDoc(ref, {
      blockedSlots: [...blocked],
      [`slotBlockReasons.${slotTime.replace(":", "_")}`]:
        reason || "Admin blocked",
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  }

  /** Unblock a previously blocked time slot */
  async unblockSlot(serviceId, dateStr, slotTime) {
    const avail = await this.getOrCreateAvailability(serviceId, dateStr);
    const blocked = (avail.blockedSlots || []).filter((t) => t !== slotTime);
    const ref = doc(this.availabilityRef, avail.id);
    await updateDoc(ref, {
      blockedSlots: blocked,
      [`slotBlockReasons.${slotTime.replace(":", "_")}`]: "",
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  }

  /* ─────────────────────────────────────────────────────────
   * BOOKINGS – ADMIN READ
   * ───────────────────────────────────────────────────────── */

  /** Get ALL bookings (including external), newest first */
  async getAllBookings(filters = {}) {
    try {
      // No orderBy on Firestore — sort client-side to avoid needing composite indexes.
      let q = this.bookingsRef;
      if (filters.status) {
        q = query(this.bookingsRef, where("status", "==", filters.status));
      }
      if (filters.serviceId) {
        q = query(
          this.bookingsRef,
          where("serviceId", "==", filters.serviceId),
        );
      }

      // Use getDocsFromServer to bypass local persistence cache.
      // This is critical: onSnapshot fires first, then loadCalendar() calls
      // getAllBookings(). Without server reads, getDocs() can return stale
      // cached values and the revenue/stats never update.
      const [snap, extSnap] = await Promise.all([
        getDocsFromServer(q),
        getDocsFromServer(this.externalBookingsRef),
      ]);

      const regular = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const external = extSnap.docs.map((d) => {
        const e = { id: d.id, ...d.data() };
        return {
          id: e.id,
          _isExternal: true,
          bookingReference: e.referenceId
            ? `EXT-${e.referenceId}`
            : `EXT-${e.id.slice(0, 6).toUpperCase()}`,
          date: e.date,
          timeSlot: e.timeSlot || null,
          serviceId: e.serviceId,
          serviceType: e.serviceId,
          customerInfo: { name: e.guestName, email: "" },
          numberOfGuests: e.guestCount,
          totalPrice: e.price || 0,
          status: e.status,
          createdAt: e.createdAt,
          platform: e.platform,
          source: "external",
          notes: e.notes,
        };
      });

      const filteredExternal = filters.status
        ? external.filter((e) => e.status === filters.status)
        : external;

      return [...regular, ...filteredExternal].sort((a, b) => {
        const aT = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bT = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bT - aT;
      });
    } catch (err) {
      console.error("Error fetching all bookings:", err);
      return [];
    }
  }

  /** Get bookings for a specific date (all services) */
  async getBookingsForDate(dateStr) {
    try {
      const q = query(this.bookingsRef, where("date", "==", dateStr));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error("Error fetching bookings for date:", err);
      return [];
    }
  }

  /** Get bookings in a date range */
  async getBookingsInRange(startDate, endDate) {
    try {
      const q = query(
        this.bookingsRef,
        where("date", ">=", startDate),
        where("date", "<=", endDate),
      );
      const snap = await getDocs(q);
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.date > b.date ? 1 : -1));
    } catch (err) {
      console.error("Error fetching bookings in range:", err);
      return [];
    }
  }

  /* ─────────────────────────────────────────────────────────
   * BOOKINGS – ADMIN WRITE
   * ───────────────────────────────────────────────────────── */

  /** Confirm a pending booking */
  async confirmBooking(bookingId) {
    return this._setBookingStatus(bookingId, "confirmed");
  }

  /** Cancel a booking and release its capacity */
  async cancelBooking(bookingId) {
    const ref = doc(this.bookingsRef, bookingId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Booking not found");
    const data = snap.data();

    await updateDoc(ref, { status: "cancelled", updatedAt: Timestamp.now() });

    // Recalculate from live bookings so calendar is always accurate
    await this._recalculateAvailability(data.serviceId, data.date);
    return { success: true };
  }

  /** Mark a booking as completed */
  async completeBooking(bookingId) {
    return this._setBookingStatus(bookingId, "completed");
  }

  /** Delete a booking entirely (admin only) */
  async deleteBooking(bookingId) {
    const ref = doc(this.bookingsRef, bookingId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Booking not found");
    const data = snap.data();
    await deleteDoc(ref);
    // Recalculate after deleting so the doc is already gone from the count
    await this._recalculateAvailability(data.serviceId, data.date);
    return { success: true };
  }

  /* ─────────────────────────────────────────────────────────
   * EXTERNAL BOOKINGS (Airbnb, Booking.com, etc.)
   * ───────────────────────────────────────────────────────── */

  /** Create a new external booking and update availability */
  async addExternalBooking(data) {
    const {
      platform,
      guestName,
      guestEmail,
      guestPhone,
      guestCount,
      date,
      timeSlot,
      serviceId,
      notes,
      price,
      status = "confirmed",
      referenceId,
      extras,
    } = data;
    const ref = await addDoc(this.externalBookingsRef, {
      platform,
      guestName,
      guestEmail: guestEmail || "",
      guestPhone: guestPhone || "",
      guestCount: Number(guestCount),
      date,
      timeSlot: timeSlot || null,
      serviceId,
      notes: notes || "",
      price: Number(price) || 0,
      status,
      referenceId: referenceId || "",
      extras: extras || { drinks: {}, snacks: {}, jetSki: false },
      source: "external",
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    // Recalculate from all live bookings — always accurate
    await this._recalculateAvailability(serviceId, date);
    return { success: true, id: ref.id };
  }

  /** Update an external booking; recalculates availability from live data */
  async updateExternalBooking(id, updates, prevData) {
    const merged = { ...prevData, ...updates };
    const ref = doc(this.externalBookingsRef, id);
    await updateDoc(ref, { ...updates, updatedAt: Timestamp.now() });
    // Recalculate availability for both old and new dates/services
    await this._recalculateAvailability(merged.serviceId, merged.date);
    if (
      prevData.date !== merged.date ||
      prevData.serviceId !== merged.serviceId
    ) {
      await this._recalculateAvailability(prevData.serviceId, prevData.date);
    }
    return { success: true };
  }

  /** Delete an external booking and release its capacity */
  async deleteExternalBooking(id) {
    const ref = doc(this.externalBookingsRef, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("External booking not found");
    const { serviceId, date } = snap.data();
    // Delete first so it's excluded from the recalculation below
    await deleteDoc(ref);
    // Recalculate availability from live bookings — fixes the user-facing calendar
    await this._recalculateAvailability(serviceId, date);
    return { success: true };
  }

  /** Fetch all external bookings, newest first */
  async getExternalBookings(filters = {}) {
    try {
      // Use simple collection scan — no orderBy to avoid missing Firestore indexes.
      // Sort by createdAt descending client-side instead.
      let q = this.externalBookingsRef;
      if (filters.platform) {
        q = query(
          this.externalBookingsRef,
          where("platform", "==", filters.platform),
        );
      }
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort newest first client-side
      docs.sort((a, b) => {
        const ta = a.createdAt?.seconds ?? 0;
        const tb = b.createdAt?.seconds ?? 0;
        return tb - ta;
      });
      return docs;
    } catch (err) {
      console.error("Error fetching external bookings:", err);
      return [];
    }
  }

  /** Fetch external bookings for a specific date */
  async getExternalBookingsForDate(dateStr) {
    try {
      const q = query(this.externalBookingsRef, where("date", "==", dateStr));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error("Error fetching external bookings for date:", err);
      return [];
    }
  }

  /* ─────────────────────────────────────────────────────────
   * CALENDAR AGGREGATE STATS
   * ───────────────────────────────────────────────────────── */

  /**
   * Build day-level summary objects for a month, used by the calendar view.
   * Returns an array of { date, bookingCount, bookedCapacity, totalCapacity, blocked, fill }
   */
  async getMonthSummary(year, month, serviceFilter = null) {
    const pad = (n) => String(n).padStart(2, "0");
    const start = `${year}-${pad(month)}-01`;
    const last = new Date(year, month, 0).getDate();
    const end = `${year}-${pad(month)}-${pad(last)}`;

    const [bookings, avails, blockedList, externalBookings] = await Promise.all(
      [
        this.getBookingsInRange(start, end),
        this._getAvailabilityRange(start, end),
        this.getBlockedDates(),
        this._getExternalBookingsInRange(start, end),
      ],
    );

    const blockedSet = new Set(blockedList.map((b) => b.date));

    // Only count active (non-cancelled) bookings
    const _allActive = [
      ...bookings.filter((b) => b.status !== "cancelled"),
      ...externalBookings.filter((b) => b.status !== "cancelled"),
    ];
    const activeBookings = serviceFilter
      ? _allActive.filter((b) => b.serviceId === serviceFilter)
      : _allActive;

    // group bookings by date (for bookingCount display — active only)
    const bookingsByDate = {};
    activeBookings.forEach((b) => {
      if (!bookingsByDate[b.date]) bookingsByDate[b.date] = [];
      bookingsByDate[b.date].push(b);
    });

    // Compute real booked guests per date+serviceId from actual active bookings
    // This is the ground-truth and avoids stale stored counters in availability docs
    const realBookedPerKey = {};
    activeBookings.forEach((b) => {
      if (!b.serviceId || !b.date) return;
      const key = `${b.date}_${b.serviceId}`;
      realBookedPerKey[key] =
        (realBookedPerKey[key] || 0) +
        (Number(b.guestCount) || Number(b.numberOfGuests) || 0);
    });

    // group availability by date AND serviceId (used for totalCapacity + timeSlots only)
    const availByDateAndService = {};
    avails.forEach((a) => {
      if (!availByDateAndService[a.date]) availByDateAndService[a.date] = {};
      availByDateAndService[a.date][a.serviceId] = a;
    });

    const KNOWN_SERVICES = serviceFilter
      ? [serviceFilter]
      : ["speedboat", "bayliner", "jet-ski", "luxury-yacht"];

    // Precompute default capacity per service (used when no availability doc exists)
    const defaultCapByService = {};
    KNOWN_SERVICES.forEach((svcId) => {
      defaultCapByService[svcId] = getServiceDefaultSlots(svcId).reduce(
        (sum, s) => sum + s.capacity,
        0,
      );
    });

    const days = [];
    for (let d = 1; d <= last; d++) {
      const date = `${year}-${pad(month)}-${pad(d)}`;
      const bks = bookingsByDate[date] || [];
      const dateServicesMap = availByDateAndService[date] || {};

      // Only aggregate capacity for services that have an availability doc OR
      // active bookings on this date — prevents inflating the total with
      // unrelated services (e.g. adding jet-ski + yacht capacity to a day
      // that only has speedboat bookings).
      let totalCapacity = 0;
      KNOWN_SERVICES.forEach((svcId) => {
        const svc = dateServicesMap[svcId];
        const hasBookings = (realBookedPerKey[`${date}_${svcId}`] || 0) > 0;
        // Only include a service's capacity if it has actual bookings on this date.
        // Stale availability docs (created as side-effects of getOrCreateAvailability
        // during _recalculateAvailability) must not inflate the total.
        if (hasBookings) {
          // Always derive from timeSlots so stale stored totalCapacity is ignored
          const svcCap = svc
            ? svc.timeSlots && svc.timeSlots.length > 0
              ? svc.timeSlots.reduce((sum, s) => sum + (s.capacity || 0), 0)
              : defaultCapByService[svcId]
            : defaultCapByService[svcId];
          totalCapacity += svcCap;
        }
      });
      // Use real guest counts from actual bookings, not the stored counter
      const bookedCapacity = KNOWN_SERVICES.reduce((sum, svcId) => {
        return sum + (realBookedPerKey[`${date}_${svcId}`] || 0);
      }, 0);
      const fill = totalCapacity > 0 ? bookedCapacity / totalCapacity : 0;
      const slotsLeft = Math.max(0, totalCapacity - bookedCapacity);

      // Per-service breakdown for calendar dots
      const services = {};
      KNOWN_SERVICES.forEach((svcId) => {
        const svc = dateServicesMap[svcId];
        const realBooked = realBookedPerKey[`${date}_${svcId}`] || 0;
        const svcCap = svc
          ? svc.timeSlots && svc.timeSlots.length > 0
            ? svc.timeSlots.reduce((sum, s) => sum + (s.capacity || 0), 0)
            : defaultCapByService[svcId]
          : defaultCapByService[svcId];
        const svcFill = svcCap > 0 ? realBooked / svcCap : 0;
        services[svcId] = {
          fill: svcFill,
          bookedCapacity: realBooked,
          totalCapacity: svcCap,
        };
      });

      days.push({
        date,
        bookingCount: bks.length,
        bookedCapacity,
        totalCapacity,
        blocked: blockedSet.has(date) || blockedSet.has(`all_${date}`),
        fill,
        slotsLeft,
        services,
        // keep timeSlots from first available service for backward compat
        timeSlots: Object.values(dateServicesMap)[0]?.timeSlots || [],
      });
    }

    // Self-heal stale availability docs — write back correct bookedCapacity
    // whenever it differs from the real count. The user-facing calendar's
    // onSnapshot listener picks this up instantly without any page reload.
    try {
      const staleAvails = avails.filter((a) => {
        const realBooked = realBookedPerKey[`${a.date}_${a.serviceId}`] || 0;
        return a.bookedCapacity !== realBooked;
      });
      if (staleAvails.length > 0) {
        await Promise.all(
          staleAvails.map(async (a) => {
            const realBooked =
              realBookedPerKey[`${a.date}_${a.serviceId}`] || 0;
            // Build per-slot booked counts from active bookings
            const slotBooked = {};
            activeBookings.forEach((b) => {
              if (
                b.serviceId === a.serviceId &&
                b.date === a.date &&
                b.timeSlot
              ) {
                slotBooked[b.timeSlot] =
                  (slotBooked[b.timeSlot] || 0) +
                  (Number(b.guestCount) || Number(b.numberOfGuests) || 0);
              }
            });
            const fixedSlots = (a.timeSlots || []).map((s) => ({
              ...s,
              booked: slotBooked[s.time] || 0,
            }));
            await updateDoc(doc(this.availabilityRef, a.id), {
              bookedCapacity: realBooked,
              timeSlots: fixedSlots,
              updatedAt: Timestamp.now(),
            });
          }),
        );
      }
    } catch (healErr) {
      console.warn("Self-heal availability:", healErr);
    }

    return days;
  }

  /* ─────────────────────────────────────────────────────────
   * PRIVATE HELPERS
   * ───────────────────────────────────────────────────────── */

  async _setBookingStatus(bookingId, status) {
    const ref = doc(this.bookingsRef, bookingId);
    await updateDoc(ref, { status, updatedAt: Timestamp.now() });
    return { success: true };
  }

  async _occupyCapacity(serviceId, date, timeSlot, numberOfGuests) {
    try {
      const avail = await this.getOrCreateAvailability(serviceId, date);
      const ref = doc(this.availabilityRef, avail.id);
      if (timeSlot) {
        const idx = (avail.timeSlots || []).findIndex(
          (s) => s.time === timeSlot,
        );
        if (idx !== -1) {
          const slots = [...avail.timeSlots];
          slots[idx] = {
            ...slots[idx],
            booked: (slots[idx].booked || 0) + numberOfGuests,
          };
          await updateDoc(ref, {
            timeSlots: slots,
            bookedCapacity: (avail.bookedCapacity || 0) + numberOfGuests,
            updatedAt: Timestamp.now(),
          });
        } else {
          await updateDoc(ref, {
            bookedCapacity: (avail.bookedCapacity || 0) + numberOfGuests,
            updatedAt: Timestamp.now(),
          });
        }
      } else {
        await updateDoc(ref, {
          bookedCapacity: (avail.bookedCapacity || 0) + numberOfGuests,
          updatedAt: Timestamp.now(),
        });
      }
    } catch (err) {
      console.error("Error occupying capacity:", err);
    }
  }

  /**
   * Recompute availability for serviceId+date from actual live bookings.
   * Called after any delete/cancel so stale counters can never persist.
   * The real-time onSnapshot listener in useAvailability picks up the update.
   */
  async _recalculateAvailability(serviceId, date) {
    try {
      const [regularSnap, externalSnap] = await Promise.all([
        getDocs(
          query(
            this.bookingsRef,
            where("serviceId", "==", serviceId),
            where("date", "==", date),
          ),
        ),
        getDocs(
          query(
            this.externalBookingsRef,
            where("serviceId", "==", serviceId),
            where("date", "==", date),
          ),
        ),
      ]);

      const activeBookings = [
        ...regularSnap.docs
          .map((d) => d.data())
          .filter((b) => b.status !== "cancelled"),
        ...externalSnap.docs
          .map((d) => d.data())
          .filter((b) => b.status !== "cancelled"),
      ];

      const avail = await this.getOrCreateAvailability(serviceId, date);
      const ref = doc(this.availabilityRef, avail.id);

      // Sum guests per time slot
      const slotBooked = {};
      let totalBooked = 0;
      activeBookings.forEach((b) => {
        const guests = Number(b.guestCount) || Number(b.numberOfGuests) || 0;
        totalBooked += guests;
        const slot = b.timeSlot;
        if (slot) slotBooked[slot] = (slotBooked[slot] || 0) + guests;
      });

      // Use default slot structure when the doc has no slots yet (new date)
      const slotBase = avail.timeSlots?.length
        ? avail.timeSlots
        : getServiceDefaultSlots(serviceId);

      // Compute unslotted guests (bookings with no specific time slot)
      const slottedTotal = Object.values(slotBooked).reduce((s, v) => s + v, 0);
      const unslottedGuests = totalBooked - slottedTotal;

      let updatedSlots = slotBase.map((s) => ({
        ...s,
        booked: slotBooked[s.time] || 0,
      }));
      // Assign unslotted guests to the first slot with remaining capacity
      // so Firestore slot data matches what useAvailability computes
      if (unslottedGuests > 0 && updatedSlots.length > 0) {
        const idx = updatedSlots.findIndex((s) => s.booked < s.capacity);
        const target = idx >= 0 ? idx : 0;
        updatedSlots = updatedSlots.map((s, i) =>
          i === target ? { ...s, booked: s.booked + unslottedGuests } : s,
        );
      }

      await updateDoc(ref, {
        timeSlots: updatedSlots,
        bookedCapacity: totalBooked,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error("Error recalculating availability:", err);
    }
  }

  async _releaseCapacity(serviceId, date, timeSlot, numberOfGuests) {
    try {
      const q = query(
        this.availabilityRef,
        where("serviceId", "==", serviceId),
        where("date", "==", date),
      );
      const snap = await getDocs(q);
      if (snap.empty) return;

      const ref = snap.docs[0].ref;
      const data = snap.docs[0].data();

      // Always decrement bookedCapacity at the day level
      const updateData = {
        bookedCapacity: Math.max(
          0,
          (data.bookedCapacity || 0) - numberOfGuests,
        ),
        updatedAt: Timestamp.now(),
      };

      // Also decrement the per-slot counter if the slot is found
      if (timeSlot && data.timeSlots) {
        const idx = data.timeSlots.findIndex((s) => s.time === timeSlot);
        if (idx !== -1) {
          const slots = [...data.timeSlots];
          slots[idx] = {
            ...slots[idx],
            booked: Math.max(0, (slots[idx].booked || 0) - numberOfGuests),
          };
          updateData.timeSlots = slots;
        }
      }

      await updateDoc(ref, updateData);
    } catch (err) {
      console.error("Error releasing capacity:", err);
    }
  }

  async _getAvailabilityRange(start, end) {
    try {
      const q = query(
        this.availabilityRef,
        where("date", ">=", start),
        where("date", "<=", end),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error("Error fetching availability range:", err);
      return [];
    }
  }

  async _getExternalBookingsInRange(start, end) {
    try {
      const q = query(
        this.externalBookingsRef,
        where("date", ">=", start),
        where("date", "<=", end),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.error("Error fetching external bookings range:", err);
      return [];
    }
  }
}

const adminCalendarService = new AdminCalendarService();
export default adminCalendarService;
